import { z } from 'zod';

import { MAX_AGE, MIN_AGE, SQUAD_DEFAULTS } from '../constants/squad';
import { LevelSchema, WeekdaySchema } from '../enums';
import { AvailabilitySchema, HHmmSchema, IdSchema, PhoneSchema } from './common';

export const UserLevelInputSchema = z.object({
  activityId: IdSchema,
  level: LevelSchema,
});
export type UserLevelInput = z.infer<typeof UserLevelInputSchema>;

/** Год рождения относительно текущего года (18–80 лет). */
export function birthYearSchema(now: Date = new Date()) {
  const year = now.getUTCFullYear();
  return z
    .number()
    .int()
    .min(year - MAX_AGE, `Год рождения не раньше ${year - MAX_AGE}`)
    .max(year - MIN_AGE, `Только с ${MIN_AGE} лет`);
}

/**
 * Онбординг из 5 экранов (BLUEPRINT 3.2): город → районы → вечера → активности и уровни → о себе.
 * `squadSize` должен входить в `sizeOptions` основной активности — проверяется на сервере по каталогу.
 *
 * `primaryActivityId = null` — пользователь предложил свою активность и ждёт решения ops
 * (Stage 3, задача 1a): `PoolApplication` не создаётся, сервер проверяет наличие PENDING-предложения.
 */
export function onboardingInputSchema(now: Date = new Date()) {
  return z
    .object({
      cityId: IdSchema,
      districtIds: z.array(IdSchema).min(1, 'Выбери хотя бы один район').max(5),
      availability: AvailabilitySchema,
      primaryActivityId: IdSchema.nullable(),
      squadSize: z
        .number()
        .int()
        .min(SQUAD_DEFAULTS.proposalSizeMin)
        .max(SQUAD_DEFAULTS.proposalSizeMax)
        .nullable(),
      /** Уровни по выбранным активностям; основная активность обязательна, если выбрана. */
      levels: z.array(UserLevelInputSchema).max(12),
      /** Неосновные интересы (баня, квиз и т. п.) */
      interestActivityIds: z.array(IdSchema).max(12).default([]),
      birthYear: birthYearSchema(now),
      phone: PhoneSchema.optional(),
      /**
       * Дополнительные наборы, кроме основного: человек может стоять в нескольких очередях
       * сразу (лимит `squad.maxPoolApplications`). Размер отряда у каждой активности свой,
       * уровень берётся из `levels` или из уже сохранённого `UserLevel`.
       */
      extraChoices: z
        .array(z.object({ activityId: IdSchema, squadSize: z.number().int().min(2).max(16) }))
        .max(3)
        .default([]),
      agreedRules: z.literal(true, {
        errorMap: () => ({ message: 'Нужно принять правила состава' }),
      }),
      agreedPrivacy: z.literal(true, {
        errorMap: () => ({ message: 'Нужно согласие на обработку персональных данных' }),
      }),
    })
    .superRefine((v, ctx) => {
      // Дополнительный набор не может повторять основной и другой дополнительный: заявка в пуле
      // одна на активность, вторая по той же активности отклонится сервером.
      const extraIds = v.extraChoices.map((c) => c.activityId);
      if (new Set(extraIds).size !== extraIds.length || extraIds.includes(v.primaryActivityId ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['extraChoices'],
          message: 'Активность уже выбрана',
        });
      }
      if (v.primaryActivityId) {
        if (v.squadSize === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['squadSize'],
            message: 'Выбери размер отряда',
          });
        }
        if (!v.levels.some((l) => l.activityId === v.primaryActivityId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['levels'],
            message: 'Укажи уровень по основной активности',
          });
        }
      }
      const ids = v.levels.map((l) => l.activityId);
      if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['levels'],
          message: 'Уровень по каждой активности указывается один раз',
        });
      }
    });
}

export const OnboardingInputSchema = onboardingInputSchema();
export type OnboardingInput = z.infer<typeof OnboardingInputSchema>;

// ───────────────────────────── onboarding.options ─────────────────────────────

export const CityOptionSchema = z.object({
  id: IdSchema,
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
});
export type CityOption = z.infer<typeof CityOptionSchema>;

export const DistrictOptionSchema = z.object({
  id: IdSchema,
  cityId: IdSchema,
  name: z.string(),
  slug: z.string(),
});
export type DistrictOption = z.infer<typeof DistrictOptionSchema>;

/** Активность каталога города для выбора при онбординге и в «открытых наборах». */
export const ActivityOptionSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isPrimary: z.boolean(),
  sizeOptions: z.array(z.number().int().positive()),
  minPlay: z.number().int().nonnegative(),
  needsLevel: z.boolean(),
  source: z.enum(['CATALOG', 'PROPOSED']),
  reserveSlots: z.number().int().nonnegative(),
  rotationHint: z.string().nullable(),
});
export type ActivityOption = z.infer<typeof ActivityOptionSchema>;

export const TimeWindowOptionSchema = z.object({
  code: z.string(),
  from: HHmmSchema,
  to: HHmmSchema,
});

export const OnboardingOptionsSchema = z.object({
  cities: z.array(CityOptionSchema),
  /** Районы выбранного (или единственного) города */
  districts: z.array(DistrictOptionSchema),
  /** Основные активности города (isPrimary) — для выбора основной */
  primaryActivities: z.array(ActivityOptionSchema),
  /** Неосновные — интересы */
  interestActivities: z.array(ActivityOptionSchema),
  timeWindows: z.array(TimeWindowOptionSchema),
  weekdays: z.array(WeekdaySchema),
  /** Предложения пользователя в статусе PENDING — можно завершить онбординг без основной активности */
  pendingProposalIds: z.array(IdSchema),
});
export type OnboardingOptions = z.infer<typeof OnboardingOptionsSchema>;
